import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography, Chip, Stack } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import MonitorIcon from '@mui/icons-material/Monitor';

const steps = [
  {
    num: '01',
    icon: <GetAppIcon />,
    title: 'Download the App',
    desc: 'Get the AutoChecker driver app from the App Store or Google Play. Quick installation — no technical setup required for drivers.',
    color: '#1075BA',
    image: 'download',
  },
  {
    num: '02',
    icon: <PersonAddIcon />,
    title: 'Create an Account',
    desc: 'Register your fleet account in minutes. Add your company details, invite drivers, and configure your vehicles from one central place.',
    color: '#F8B220',
    image: 'account',
  },
  {
    num: '03',
    icon: <PhoneAndroidIcon />,
    title: 'Use the App',
    desc: 'Drivers simply open the app and start their trip. AutoChecker silently tracks location, speed, and behavior in the background.',
    color: '#1075BA',
    image: 'use',
  },
  {
    num: '04',
    icon: <MonitorIcon />,
    title: 'Monitor in the Dashboard',
    desc: 'Watch your entire fleet live on the admin dashboard. Get instant alerts, run reports, and make data-driven decisions with ease.',
    color: '#F8B220',
    image: 'dashboard',
  },
];

function DownloadIllustration() {
  return (
    <svg viewBox="0 0 380 280" width="100%" height="100%">
      <rect x="120" y="10" width="140" height="260" rx="22" fill="#0d1f35"/>
      <rect x="130" y="24" width="120" height="232" rx="14" fill="#132338"/>
      <rect x="140" y="38" width="100" height="120" rx="10" fill="#0d2d4a"/>
      {[[148,46],[172,46],[196,46],[148,70],[172,70],[196,70],[148,94],[172,94]].map(([x,y],i)=>(
        <rect key={i} x={x} y={y} width="20" height="20" rx="6" fill={i===4?'#1075BA':'rgba(255,255,255,0.08)'}/>
      ))}
      <text x="182" y="111" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">↓</text>
      <rect x="140" y="172" width="100" height="7" rx="3.5" fill="rgba(255,255,255,0.08)"/>
      <rect x="140" y="172" width="68" height="7" rx="3.5" fill="#1075BA"/>
      <text x="190" y="196" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="8">Downloading...</text>
      <rect x="155" y="248" width="50" height="3" rx="1.5" fill="rgba(255,255,255,0.25)"/>
      <rect x="60" y="208" width="80" height="32" rx="10" fill="#F8B220"/>
      <text x="100" y="228" textAnchor="middle" fill="#0d1f35" fontSize="9" fontWeight="bold">App Store</text>
      <rect x="240" y="208" width="80" height="32" rx="10" fill="#1075BA"/>
      <text x="280" y="228" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">Google Play</text>
      <rect x="10" y="80" width="96" height="48" rx="12" fill="white" opacity="0.95"/>
      <text x="28" y="102" fill="#F8B220" fontSize="12">★★★★★</text>
      <text x="28" y="118" fill="#0d1f35" fontSize="8" fontWeight="700">4.9 rating</text>
      <rect x="274" y="120" width="96" height="48" rx="12" fill="white" opacity="0.95"/>
      <text x="322" y="140" textAnchor="middle" fill="#1075BA" fontSize="16" fontWeight="800">15K+</text>
      <text x="322" y="157" textAnchor="middle" fill="#4a6580" fontSize="8">Downloads</text>
    </svg>
  );
}

function AccountIllustration() {
  return (
    <svg viewBox="0 0 380 280" width="100%" height="100%">
      <rect x="50" y="10" width="280" height="258" rx="20" fill="white" opacity="0.97"/>
      <rect x="50" y="10" width="280" height="52" rx="20" fill="#1075BA"/>
      <rect x="50" y="42" width="280" height="20" fill="#1075BA"/>
      <text x="190" y="43" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">Create Your Account</text>
      <circle cx="190" cy="88" r="26" fill="#eaf4ff"/>
      <circle cx="190" cy="81" r="11" fill="#1075BA"/>
      <ellipse cx="190" cy="104" rx="16" ry="9" fill="#1075BA"/>
      {[['Company Name','ARC Logistics',128],['Admin Email','admin@arc.ph',163],['Password','••••••••',198]].map(([label,val,y])=>(
        <g key={y}>
          <text x="74" y={y-5} fill="#4a6580" fontSize="8.5" fontWeight="600">{label}</text>
          <rect x="74" y={y} width="232" height="26" rx="8" fill="#f5f9ff" stroke="#1075BA" strokeWidth="1.5"/>
          <text x="86" y={y+17} fill="#0d1f35" fontSize="9.5">{val}</text>
        </g>
      ))}
      <rect x="74" y="232" width="232" height="26" rx="13" fill="#1075BA"/>
      <text x="190" y="249" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">Get Started</text>
      <rect x="5" y="70" width="36" height="36" rx="10" fill="#4ade80"/>
      <text x="23" y="93" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold">✓</text>
      <rect x="339" y="170" width="36" height="36" rx="10" fill="#F8B220"/>
      <text x="357" y="193" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold">✓</text>
    </svg>
  );
}

function UseIllustration() {
  return (
    <svg viewBox="0 0 380 280" width="100%" height="100%">
      <rect x="135" y="5" width="110" height="230" rx="20" fill="#0d1f35"/>
      <rect x="143" y="19" width="94" height="202" rx="12" fill="#0a2540"/>
      <rect x="143" y="19" width="94" height="130" rx="12" fill="#0d3460"/>
      {[29,49,69,89,109,129].map(y=><line key={y} x1="143" y1={y} x2="237" y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>)}
      {[163,183,203,223].map(x=><line key={x} x1={x} y1="19" x2={x} y2="149" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>)}
      <polyline points="155,132 168,105 190,90 215,78 228,59" fill="none" stroke="#1075BA" strokeWidth="2.5" strokeDasharray="5 3"/>
      <circle cx="190" cy="90" r="8" fill="#1075BA"/>
      <circle cx="190" cy="90" r="14" fill="rgba(16,117,186,0.28)"/>
      <circle cx="228" cy="59" r="5" fill="#F8B220"/>
      <rect x="143" y="154" width="94" height="67" fill="rgba(255,255,255,0.04)"/>
      <text x="151" y="170" fill="rgba(255,255,255,0.45)" fontSize="7.5">Speed</text>
      <text x="151" y="183" fill="#F8B220" fontSize="12" fontWeight="bold">58 km/h</text>
      <text x="193" y="170" fill="rgba(255,255,255,0.45)" fontSize="7.5">Score</text>
      <text x="193" y="183" fill="#4ade80" fontSize="12" fontWeight="bold">96</text>
      <text x="151" y="200" fill="rgba(255,255,255,0.35)" fontSize="7">Trip: 12.4km · ETA 8min</text>
      <rect x="155" y="225" width="50" height="3" rx="1.5" fill="rgba(255,255,255,0.25)"/>
      <rect x="10" y="48" width="112" height="52" rx="14" fill="white" opacity="0.97"/>
      <circle cx="32" cy="72" r="10" fill="#4ade80"/>
      <text x="32" y="77" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">✓</text>
      <text x="48" y="67" fill="#0d1f35" fontSize="9" fontWeight="700">Trip Started</text>
      <text x="48" y="80" fill="#4a6580" fontSize="7.5">Background tracking on</text>
      <text x="60" y="93" fill="#4a6580" fontSize="7">AutoChecker is monitoring</text>
      <rect x="258" y="155" width="112" height="52" rx="14" fill="white" opacity="0.97"/>
      <circle cx="280" cy="179" r="14" fill="#1075BA"/>
      <text x="280" y="184" textAnchor="middle" fill="white" fontSize="11">👤</text>
      <text x="300" y="174" fill="#0d1f35" fontSize="9" fontWeight="700">Juan D.</text>
      <text x="300" y="186" fill="#4a6580" fontSize="7.5">Driver Active</text>
      <text x="300" y="198" fill="#F8B220" fontSize="8" fontWeight="700">★ 4.9 Score</text>
    </svg>
  );
}

function DashboardIllustration() {
  return (
    <svg viewBox="0 0 380 280" width="100%" height="100%">
      <rect x="10" y="5" width="360" height="270" rx="16" fill="#0d1f35"/>
      <rect x="10" y="5" width="360" height="38" rx="16" fill="#1a3a5c"/>
      <rect x="10" y="32" width="360" height="11" fill="#1a3a5c"/>
      {[['#ef4444',32],['#F8B220',48],['#4ade80',64]].map(([c,x])=><circle key={x} cx={x} cy="23" r="5" fill={c}/>)}
      <rect x="98" y="13" width="184" height="20" rx="5" fill="rgba(255,255,255,0.07)"/>
      <text x="190" y="27" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="8">app.autochecker.io/dashboard</text>
      {[['Active','42','#4ade80',25],['Idle','7','#F8B220',105],['Alerts','3','#ef4444',185],['Score','94','#1075BA',265]].map(([l,v,c,x])=>(
        <g key={x}>
          <rect x={x} y="54" width="70" height="46" rx="10" fill="rgba(255,255,255,0.05)"/>
          <text x={x+35} y="74" textAnchor="middle" fill={c} fontSize="17" fontWeight="800">{v}</text>
          <text x={x+35} y="89" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7.5">{l}</text>
        </g>
      ))}
      <rect x="25" y="110" width="195" height="120" rx="10" fill="#0a2540"/>
      {[120,140,160,180,200,220].map(y=><line key={y} x1="25" y1={y} x2="220" y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>)}
      {[[65,145],[110,170],[150,140],[90,205]].map(([x,y],i)=>(
        <circle key={i} cx={x} cy={y} r="5" fill={i%2===0?'#1075BA':'#F8B220'} opacity="0.9"/>
      ))}
      <polyline points="65,145 110,170 150,140" fill="none" stroke="#1075BA" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.6"/>
      <text x="122" y="224" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="7.5">Live Fleet Map</text>
      <rect x="230" y="110" width="140" height="120" rx="10" fill="rgba(255,255,255,0.03)"/>
      <text x="300" y="127" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="7.5" fontWeight="600">LIVE ACTIVITY</text>
      {[['FL-2047 entered Zone A','#4ade80',143],['Low fuel: FL-1023','#F8B220',162],['Speed alert FL-3301','#ef4444',181],['FL-0091 trip started','#1075BA',200]].map(([msg,c,y])=>(
        <g key={y}>
          <circle cx="242" cy={y-3} r="3" fill={c}/>
          <text x="251" y={y} fill="rgba(255,255,255,0.7)" fontSize="7.5">{msg}</text>
        </g>
      ))}
      <rect x="25" y="242" width="335" height="24" rx="8" fill="rgba(255,255,255,0.03)"/>
      {[0,1,2,3,4,5,6].map(i=>(
        <rect key={i} x={38+i*46} y={247+[6,3,9,1,5,8,3][i]} width="24" height={[14,17,10,20,14,10,17][i]} rx="3" fill={i===3?'#F8B220':'rgba(16,117,186,0.45)'}/>
      ))}
    </svg>
  );
}

const illustrations = { download: DownloadIllustration, account: AccountIllustration, use: UseIllustration, dashboard: DashboardIllustration };

function Step({ step, index, isLast, isActive, onHover }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.2 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      onMouseEnter={onHover}
      sx={{
        display: 'flex', gap: 3, alignItems: 'flex-start',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateX(-28px)',
        transition: `opacity 0.6s ${index * 0.12}s ease, transform 0.6s ${index * 0.12}s ease`,
        cursor: 'default',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{
          width: 58, height: 58, borderRadius: '16px',
          background: isActive
            ? (step.color === '#1075BA' ? 'linear-gradient(135deg, #1075BA, #3a9de4)' : 'linear-gradient(135deg, #F8B220, #ffc94d)')
            : '#fff',
          border: `2px solid ${isActive ? 'transparent' : step.color + '28'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: isActive ? `0 8px 28px ${step.color}42` : '0 2px 10px rgba(0,0,0,0.05)',
          transition: 'all 0.32s cubic-bezier(0.34,1.56,0.64,1)',
          '& .MuiSvgIcon-root': { color: isActive ? '#fff' : step.color, fontSize: 24, transition: 'color 0.3s' },
          position: 'relative', flexShrink: 0,
        }}>
          {step.icon}
          <Box sx={{
            position: 'absolute', top: -9, right: -9,
            width: 21, height: 21, borderRadius: '50%',
            bgcolor: isActive ? step.color : '#fff',
            border: `2px solid ${step.color}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.3s ease',
          }}>
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: isActive ? '#fff' : step.color, lineHeight: 1 }}>{step.num}</Typography>
          </Box>
        </Box>
        {!isLast && (
          <Box sx={{ width: 2, flexGrow: 1, minHeight: 40, mt: 1, background: `linear-gradient(to bottom, ${step.color}40, rgba(16,117,186,0.12))`, borderRadius: 2 }} />
        )}
      </Box>

      <Box sx={{ pb: isLast ? 0 : 4 }}>
        <Typography sx={{ fontWeight: 700, color: isActive ? step.color : '#0d1f35', mb: 0.75, fontSize: '1.1rem', transition: 'color 0.3s' }}>
          {step.title}
        </Typography>
        <Typography sx={{ color: '#4a6580', lineHeight: 1.75, fontSize: '0.88rem', maxWidth: 390 }}>
          {step.desc}
        </Typography>
      </Box>
    </Box>
  );
}

export default function HowItWorks() {
  const [titleVisible, setTitleVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const titleRef = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setTitleVisible(true); obs.disconnect(); } }, { threshold: 0.3 });
    if (titleRef.current) obs.observe(titleRef.current);
    return () => obs.disconnect();
  }, []);

  const handleHover = (index) => {
    if (index === activeStep) return;
    setAnimating(true);
    setTimeout(() => { setActiveStep(index); setAnimating(false); }, 200);
  };

  const Illustration = illustrations[steps[activeStep].image];

  return (
    <Box id="how-it-works" sx={{ py: { xs: 10, md: 14 }, background: 'linear-gradient(160deg, #f5f9ff 0%, #eaf4ff 100%)', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(248,178,32,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <Container maxWidth="lg">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 8, md: 10 }, alignItems: 'center' }}>

          {/* Left: steps */}
          <Box>
            <Box ref={titleRef} sx={{ mb: 6, opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'none' : 'translateY(20px)', transition: 'all 0.7s ease' }}>
              <Chip label="⚙️ Simple Setup" sx={{ mb: 2.5, bgcolor: 'rgba(248,178,32,0.12)', color: '#c88c00', fontWeight: 700, border: '1px solid rgba(248,178,32,0.3)' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, color: '#0d1f35', mb: 2 }}>
                Up & Running in{' '}
                <Box component="span" sx={{ background: 'linear-gradient(135deg, #1075BA, #3a9de4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Minutes
                </Box>
              </Typography>
              <Typography sx={{ color: '#4a6580', fontSize: '1rem' }}>
                No complex installations. No IT team required. Just download and go.
              </Typography>
            </Box>

            <Stack gap={0}>
              {steps.map((step, i) => (
                <Step key={step.num} step={step} index={i} isLast={i === steps.length - 1} isActive={activeStep === i} onHover={() => handleHover(i)} />
              ))}
            </Stack>
          </Box>

          {/* Right: illustration card */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Box sx={{
              width: '100%', maxWidth: 430,
              borderRadius: '28px',
              background: 'linear-gradient(145deg, #e8f4ff 0%, #dceeff 100%)',
              border: '1.5px solid rgba(16,117,186,0.14)',
              boxShadow: '0 28px 65px rgba(16,117,186,0.13)',
              overflow: 'hidden',
              p: 2.5,
              // Card pop animation on step change
              transform: animating ? 'scale(0.97) translateY(5px)' : 'scale(1) translateY(0px)',
              transition: 'transform 0.22s cubic-bezier(0.34,1.56,0.64,1)',
            }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{
                  px: 2, py: 0.65, borderRadius: 50,
                  background: steps[activeStep].color === '#1075BA'
                    ? 'linear-gradient(135deg, #1075BA, #3a9de4)'
                    : 'linear-gradient(135deg, #F8B220, #ffc94d)',
                  boxShadow: `0 4px 14px ${steps[activeStep].color}38`,
                  transition: 'all 0.3s ease',
                }}>
                  <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.07em' }}>
                    STEP {steps[activeStep].num}
                  </Typography>
                </Box>
                <Typography sx={{
                  color: '#0d1f35', fontWeight: 700, fontSize: '0.88rem',
                  opacity: animating ? 0 : 1,
                  transform: animating ? 'translateX(6px)' : 'none',
                  transition: 'opacity 0.2s, transform 0.2s',
                }}>
                  {steps[activeStep].title}
                </Typography>
              </Box>

              {/* Illustration frame */}
              <Box sx={{
                borderRadius: '18px',
                background: 'linear-gradient(145deg, #fff 0%, #f4f9ff 100%)',
                border: '1px solid rgba(16,117,186,0.09)',
                height: 295,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
                p: 1.5,
                opacity: animating ? 0 : 1,
                transform: animating ? 'translateY(10px) scale(0.98)' : 'translateY(0) scale(1)',
                transition: 'opacity 0.2s ease, transform 0.2s ease',
              }}>
                <Illustration />
              </Box>

              {/* Dot nav */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                {steps.map((s, i) => (
                  <Box
                    key={i}
                    onClick={() => handleHover(i)}
                    sx={{
                      width: activeStep === i ? 22 : 7,
                      height: 7,
                      borderRadius: 4,
                      bgcolor: activeStep === i ? s.color : 'rgba(16,117,186,0.18)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
